import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useElementSize } from '../../hooks/useElementSize';
import { WindowContentSizeContext } from './WindowContentSizeContext';
import { CloseIcon, MaximizeIcon, RestoreIcon } from './WindowIcons';
import './WindowFrame.css';

export type WindowTheme = 'dark' | 'light';
export type WindowSizePreset = 'md' | 'lg';

export interface WindowFrameProps {
  /** Visible title in the caption bar */
  title: string;
  /** Stable id used for DOM hooks (a11y) */
  windowId: string;
  /** Stacking order from the desktop shell */
  zIndex: number;
  onClose: () => void;
  /** Raise this window (focus) when the user interacts with it */
  onFocus: () => void;
  children: ReactNode;
  theme?: WindowTheme;
  sizePreset?: WindowSizePreset;
  showMaximize?: boolean;
  className?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
}

interface Geometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 250;
const TASKBAR_HEIGHT = 30;
/** Keep a little air from the viewport edges when placing windows. */
const EDGE_MARGIN = 8;

const RESIZE_DIRS: ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function randomInRange(min: number, max: number): number {
  if (max <= min) {
    return min;
  }
  return min + Math.random() * (max - min);
}

/** Fixed open size for a preset (position decided separately). */
function getOpenSize(preset: WindowSizePreset): { width: number; height: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const usableH = Math.max(MIN_HEIGHT, vh - TASKBAR_HEIGHT - EDGE_MARGIN * 2);
  const usableW = Math.max(MIN_WIDTH, vw - EDGE_MARGIN * 2);

  if (preset === 'lg') {
    return {
      width: clamp(vw * 0.8, MIN_WIDTH, usableW),
      height: clamp(vh * 0.8, MIN_HEIGHT, usableH),
    };
  }

  return {
    width: clamp(vw - 600, MIN_WIDTH, usableW),
    height: clamp(vh - 200, MIN_HEIGHT, usableH),
  };
}

/**
 * Place a window so its **center** is at a random point in the visible UI,
 * while keeping the full frame inside the viewport (minus taskbar + margin).
 * Used only when a window first mounts (open / reopen).
 */
function getRandomOpenGeometry(preset: WindowSizePreset): Geometry {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const { width, height } = getOpenSize(preset);

  // Valid center range so the box stays fully on-screen above the taskbar.
  const minCenterX = width / 2 + EDGE_MARGIN;
  const maxCenterX = vw - width / 2 - EDGE_MARGIN;
  const minCenterY = height / 2 + EDGE_MARGIN;
  const maxCenterY = vh - TASKBAR_HEIGHT - height / 2 - EDGE_MARGIN;

  const centerX = randomInRange(minCenterX, maxCenterX);
  const centerY = randomInRange(minCenterY, maxCenterY);

  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width,
    height,
  };
}

function getFullscreenGeometry(): Geometry {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: 0,
    y: 0,
    width: Math.max(MIN_WIDTH, vw - 2),
    height: Math.max(MIN_HEIGHT, vh - TASKBAR_HEIGHT),
  };
}

/**
 * Reusable Windows-style application window chrome:
 * title bar drag, edge/corner resize, maximize from current rect, close, focus.
 * On first open, position is randomized (center in viewport, fully visible).
 */
export function WindowFrame({
  title,
  windowId,
  zIndex,
  onClose,
  onFocus,
  children,
  theme = 'dark',
  sizePreset = 'lg',
  showMaximize = true,
  className = '',
  contentClassName = '',
  contentStyle,
}: WindowFrameProps) {
  const reactId = useId();
  const shellRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const controlsClass = 'os-window__controls';
  const titlebarId = `os-window-titlebar-${windowId}-${reactId.replace(/:/g, '')}`;

  const [isMaximized, setIsMaximized] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // Random in-viewport placement only on first mount (close + reopen randomizes again).
  const [geometry, setGeometry] = useState<Geometry>(() => getRandomOpenGeometry(sizePreset));
  const [restoredGeometry, setRestoredGeometry] = useState<Geometry>(() =>
    getRandomOpenGeometry(sizePreset),
  );

  /** Live content-box size so children reflow when this OS window is resized. */
  const measuredContent = useElementSize(contentRef);
  const contentSize = useMemo(
    () => ({
      width: measuredContent.width || Math.max(0, geometry.width - 2),
      height: measuredContent.height || Math.max(0, geometry.height - 34),
    }),
    [geometry.height, geometry.width, measuredContent.height, measuredContent.width],
  );

  const shellClassName = useMemo(() => {
    const modeClass = isMaximized ? 'os-window--fullscreen' : 'os-window--windowed';
    const themeClass = `os-window--${theme}`;
    const animClass = isAnimating ? 'os-window--animating' : '';
    return ['os-window', modeClass, themeClass, animClass, className].filter(Boolean).join(' ');
  }, [className, isAnimating, isMaximized, theme]);

  const frameStyle = useMemo(
    (): CSSProperties => ({
      // Base 10 so windows stack above sandbox garage chrome / home labels (z≈3)
      // while staying under the taskbar (z≈25).
      zIndex: 10 + zIndex,
      top: geometry.y,
      left: geometry.x,
      width: geometry.width,
      height: geometry.height,
      // CSS container queries can use these too
      ['--os-window-content-width' as string]: `${contentSize.width}px`,
      ['--os-window-content-height' as string]: `${contentSize.height}px`,
    }),
    [contentSize.height, contentSize.width, geometry, zIndex],
  );

  const handleClose = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    },
    [onClose],
  );

  const handleFocus = useCallback(() => {
    onFocus();
  }, [onFocus]);

  /**
   * Maximize/restore without recentering: snapshot the live rect, then animate
   * geometry from that exact box to fullscreen (or back to the saved box).
   */
  const handleToggleMaximize = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onFocus();

      const node = shellRef.current;
      if (!node) {
        return;
      }

      if (!isMaximized) {
        const rect = node.getBoundingClientRect();
        const current: Geometry = {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        };
        setRestoredGeometry(current);
        // Lock visual origin to the current on-screen box first…
        setGeometry(current);
        setIsAnimating(true);
        // …then expand to fullscreen on the next frame so CSS transitions from here.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setGeometry(getFullscreenGeometry());
            setIsMaximized(true);
          });
        });
        return;
      }

      const target = restoredGeometry;
      setIsAnimating(true);
      setIsMaximized(false);
      setGeometry(getFullscreenGeometry());
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setGeometry(target);
        });
      });
    },
    [isMaximized, onFocus, restoredGeometry],
  );

  useEffect(() => {
    if (!isAnimating) {
      return;
    }
    const timer = window.setTimeout(() => setIsAnimating(false), 220);
    return () => window.clearTimeout(timer);
  }, [geometry, isAnimating]);

  const handleTitlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (isMaximized || event.button !== 0) {
        return;
      }
      // Don't start drag from caption buttons
      if ((event.target as HTMLElement).closest(`.${controlsClass}`)) {
        return;
      }

      event.preventDefault();
      onFocus();

      const startX = event.clientX;
      const startY = event.clientY;
      const origin = { ...geometry };
      const pointerId = event.pointerId;
      const target = event.currentTarget;
      target.setPointerCapture(pointerId);

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const maxX = Math.max(0, window.innerWidth - origin.width);
        const maxY = Math.max(0, window.innerHeight - TASKBAR_HEIGHT - 24);
        setGeometry({
          ...origin,
          x: clamp(origin.x + dx, -origin.width + 80, maxX + origin.width - 80),
          y: clamp(origin.y + dy, 0, maxY),
        });
      };

      const onUp = () => {
        target.releasePointerCapture(pointerId);
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    },
    [controlsClass, geometry, isMaximized, onFocus],
  );

  const handleResizePointerDown = useCallback(
    (dir: ResizeDir) => (event: ReactPointerEvent<HTMLDivElement>) => {
      if (isMaximized || event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onFocus();

      const startX = event.clientX;
      const startY = event.clientY;
      const origin = { ...geometry };
      const pointerId = event.pointerId;
      const target = event.currentTarget;
      target.setPointerCapture(pointerId);

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let { x, y, width, height } = origin;

        if (dir.includes('e')) {
          width = Math.max(MIN_WIDTH, origin.width + dx);
        }
        if (dir.includes('s')) {
          height = Math.max(MIN_HEIGHT, origin.height + dy);
        }
        if (dir.includes('w')) {
          const nextWidth = Math.max(MIN_WIDTH, origin.width - dx);
          x = origin.x + (origin.width - nextWidth);
          width = nextWidth;
        }
        if (dir.includes('n')) {
          const nextHeight = Math.max(MIN_HEIGHT, origin.height - dy);
          y = origin.y + (origin.height - nextHeight);
          height = nextHeight;
          if (y < 0) {
            height += y;
            y = 0;
          }
        }

        // Keep a bit of the window inside the viewport
        const maxW = window.innerWidth - 16;
        const maxH = window.innerHeight - TASKBAR_HEIGHT - 8;
        width = Math.min(width, maxW);
        height = Math.min(height, maxH);

        setGeometry({ x, y, width, height });
      };

      const onUp = () => {
        target.releasePointerCapture(pointerId);
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    },
    [geometry, isMaximized, onFocus],
  );

  return (
    <div
      ref={shellRef}
      id={windowId}
      className={shellClassName}
      style={frameStyle}
      role="dialog"
      aria-label={title}
      onMouseDown={handleFocus}
    >
      <header
        id={titlebarId}
        className="os-window__titlebar"
        onPointerDown={handleTitlePointerDown}
      >
        <div className="os-window__title">
          <span className="os-window__title-label">{title}</span>
        </div>

        <div className="os-window__drag-region" aria-hidden="true" />

        <div className={controlsClass}>
          {showMaximize ? (
            <button
              type="button"
              className="os-window__btn os-window__btn--max"
              title={isMaximized ? 'Restore' : 'Maximize'}
              aria-label={isMaximized ? 'Restore window' : 'Maximize window'}
              onClick={handleToggleMaximize}
            >
              {isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
            </button>
          ) : null}
          <button
            type="button"
            className="os-window__btn os-window__btn--close"
            title="Close"
            aria-label="Close window"
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>
      </header>

      <div
        ref={contentRef}
        className={['os-window__content', contentClassName].filter(Boolean).join(' ')}
        style={contentStyle}
      >
        <WindowContentSizeContext.Provider value={contentSize}>
          {children}
        </WindowContentSizeContext.Provider>
      </div>

      {!isMaximized
        ? RESIZE_DIRS.map((dir) => (
            <div
              key={dir}
              className={`os-window__resize os-window__resize--${dir}`}
              onPointerDown={handleResizePointerDown(dir)}
              role="presentation"
              aria-hidden="true"
            />
          ))
        : null}
    </div>
  );
}

export default WindowFrame;
